import * as Dialog from "@radix-ui/react-dialog"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react"
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Image as ImageIcon,
  LayoutGrid,
  Loader2,
  Pencil,
  Sparkles,
  Video as VideoIcon,
  Workflow,
  X,
} from "lucide-react"
import { router, usePage } from "@inertiajs/react"
import { toast } from "sonner"
import {
  useShotPanelStore,
  parseShotHash,
  isShotPanelPagePath,
  isWorkspaceEntityHash,
  composerTabForPreview,
  shotPanelHash,
  type PreviewTabKey,
  type ComposerTabKey,
  type ShotViewMode,
} from "@/stores/shot-panel-store"
import {
  ShotModalSidebar,
  type ShotModalCounts,
  type ShotModalKey,
  type ShotLeftPanelKey,
} from "@/components/shot-modal-sidebar"
import { usePendingAgentAutoSend } from "@/hooks/use-pending-agent-auto-send"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import {
  fetchShotPanelBootstrap,
  fetchShotWorkspaceDetail,
  fetchShotSiblings,
  markShotSeen,
  type ShotSiblingSummary,
} from "@/lib/workspace-api"
import { ShotPreviewView, type Scene3dEditorSession } from "@/components/shot-panel/shot-preview-view"
import type { LocationScene3dDialogHandle } from "@/components/locations/location-scene-3d-dialog"
import { shotTilePosterUrl } from "@/lib/shot-tile-poster"
import { ShotCharactersView } from "@/components/shot-panel/shot-characters-view"
import { ShotLocationView } from "@/components/shot-panel/shot-location-view"
import { ShotPropsView } from "@/components/shot-panel/shot-props-view"
import { StoryboardPromptView, type StoryboardPromptViewHandle } from "@/components/shot-panel/storyboard-prompt-view"
import { useBusyTabs } from "@/hooks/use-busy-tabs"
import { busyShotTabKeys } from "@/lib/shot-jobs"
import type { ShotGenerationRefs } from "@/lib/shot-generation-refs"
import { ShotMediaVersionsView } from "@/components/shot-panel/shot-media-versions-view"
import { ShotPostProductionVersionsView } from "@/components/shot-panel/shot-post-production-versions-view"
import { ShotEditDialog } from "@/components/shot-panel/shot-edit-dialog"
import { ShotPresetWizard } from "@/components/projects/shot-preset-wizard"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { ShotFrameFormatChip } from "@/components/shot-panel/shot-frame-format-chip"
import { ShotVideoModelChip } from "@/components/shot-panel/shot-video-model-chip"
import { AgentChatPanel, type AgentEntityUpdatedPayload } from "@/components/agent/agent-chat-panel"
import type { MentionItem } from "@/components/prompt-editor/types"
import { WorkspaceAgentSideColumn } from "@/components/workspace/workspace-agent-side-column"
import { PinnedFooterBox, PinnedTabLayers } from "@/components/workspace/pinned-tab-layers"
import { WorkspaceFullscreenPanel } from "@/components/workspace/workspace-fullscreen-panel"
import { FloatingTabPill } from "@/components/ui/floating-tab-pill"
import { AgentColumnChromeProvider } from "@/components/workspace/agent-column-chrome-context"
import { BoardEmptyState } from "@/components/workspace/board-empty-state"
import { useWorkspaceEntitySplitDesktop } from "@/components/workspace/workspace-entity-split-layout"
import { useShotFlowBoard } from "@/components/shot-panel/use-shot-flow-board"
import { ShotViewModeToggle } from "@/components/shot-panel/shot-view-mode-toggle"
import { ShotColumnStage } from "@/components/shot-panel/shot-column-stage"
import { ShotLeftColumnHostProvider } from "@/components/shot-panel/shot-left-column-host"
import { preloadsLeftColumn, shotPanelColumns, type ShotLeftColumnId } from "@/components/shot-panel/shot-panel-columns"
import { CollapsedNavPeekProvider } from "@/components/ui/sidebar-tooltip"
import { Button } from "@/components/ui/primitives"
import { cn } from "@/lib/utils"
import { projectSectionAllowed } from "@/lib/project-types"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { formatShotLabel, type AgentShotRef } from "@/lib/shot-label"
import { useUiStore } from "@/stores/ui-store"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import type { WorkspaceBaseProps } from "@/types/workspace"
import type { AgentConfirmActionResult, AgentPendingAction, Scene3dLayoutUpdated } from "@/lib/agent-api"

const LEFT_WIDTH_STORAGE_KEY = "shot-panel-left-width-pct"
const LEFT_WIDTH_DEFAULT = 40
const LEFT_WIDTH_MIN = 20
const LEFT_WIDTH_MAX = 60
const RESIZE_DRAG_THRESHOLD_PX = 4

function readStoredLeftWidthPct(): number {
  try {
    const raw = localStorage.getItem(LEFT_WIDTH_STORAGE_KEY)
    if (raw == null) return LEFT_WIDTH_DEFAULT
    const n = Number(raw)
    if (!Number.isFinite(n)) return LEFT_WIDTH_DEFAULT
    return Math.min(LEFT_WIDTH_MAX, Math.max(LEFT_WIDTH_MIN, n))
  } catch {
    return LEFT_WIDTH_DEFAULT
  }
}

function clampLeftWidthPct(pct: number): number {
  return Math.min(LEFT_WIDTH_MAX, Math.max(LEFT_WIDTH_MIN, pct))
}

type ShotMeta = {
  /** Чей это meta: при переключении шота панель первый рендер ещё держит meta прошлого. */
  shotId: number
  number: string
  sceneId: number
  sceneNumber: number
  hidden: boolean
  intentSummary: string | null
  shotKind: string
  frameFormat: string | null
  videoModelKey: string | null
  videoResolution: string | null
  characterCount: number
  propCount: number
  locationHasVariant: boolean
  storyboardVersionCount: number
  shootingBoardVersionCount: number
  videoVersionCount: number
  postProductionVersionCount: number
  generationUnseen: boolean
  hasStoryboard: boolean
  hasVideo: boolean
  hasShootingBoard: boolean
  activeVideoVersionId: number | null
  postProductionSourceVersionId: number | null
}

function buildShotMeta(
  shot: Record<string, unknown>,
  sceneContext: { sceneId: number; sceneNumber: number },
): ShotMeta {
  const str = (v: unknown) => (typeof v === "string" ? v.trim() : "")
  return {
    shotId: Number(shot.id),
    number: shot.number != null ? String(shot.number) : "—",
    sceneId: sceneContext.sceneId,
    sceneNumber: sceneContext.sceneNumber,
    hidden: shot.hidden === true || shot.status === "hidden",
    intentSummary: (shot.intent_summary as string) ?? null,
    shotKind: (shot.shot_kind as string) ?? "regular",
    frameFormat: typeof shot.frame_format === "string" ? shot.frame_format : null,
    videoModelKey: typeof shot.video_model_key === "string" ? shot.video_model_key : null,
    videoResolution: typeof shot.video_resolution === "string" ? shot.video_resolution : null,
    characterCount: Array.isArray(shot.character_ids) ? (shot.character_ids as number[]).length : 0,
    propCount: Array.isArray(shot.prop_ids) ? (shot.prop_ids as number[]).length : 0,
    locationHasVariant: shot.location_variant_id != null,
    storyboardVersionCount: (shot.storyboard_version_count as number) ?? 0,
    shootingBoardVersionCount: (shot.shooting_board_version_count as number) ?? 0,
    videoVersionCount: (shot.video_version_count as number) ?? 0,
    postProductionVersionCount: (shot.post_production_version_count as number) ?? 0,
    generationUnseen: shot.generation_unseen === true,
    hasStoryboard: str(shot.storyboard_preview_url) !== "",
    hasVideo: str(shot.video_clip_url) !== "",
    hasShootingBoard: str(shot.shooting_board_preview_url) !== "",
    activeVideoVersionId: (shot.active_video_version_id as number) ?? null,
    postProductionSourceVersionId: (shot.post_production_source_version_id as number) ?? null,
  }
}

function computeDefaultPreviewTab(m: ShotMeta): PreviewTabKey {
  if (!m.hasStoryboard && m.hasVideo) return "video"
  if (m.generationUnseen && m.hasStoryboard) return "storyboard"
  return "video"
}

function countsFromMeta(m: ShotMeta | null): ShotModalCounts {
  if (!m) return {}
  return {
    characters: m.characterCount,
    props: m.propCount,
    location: m.locationHasVariant ? 1 : 0,
    storyboard_versions: m.storyboardVersionCount,
    shooting_board_versions: m.shootingBoardVersionCount,
    video_versions: m.videoVersionCount,
    post_production_versions: m.postProductionVersionCount,
  }
}

function NavThumb({
  side,
  shot,
  onClick,
}: {
  side: "prev" | "next"
  shot: ShotSiblingSummary | null
  onClick: (id: number) => void
}) {
  const { t } = useTranslation(["shot", "common"])
  const Arrow = side === "prev" ? ArrowLeft : ArrowRight
  const disabled = !shot
  const posterUrl = shot ? shotTilePosterUrl(shot) : null
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => shot && onClick(shot.id)}
      title={
        shot
          ? shot.intent_summary
            ? t("nav.prevWithSummary", { number: shot.number, summary: shot.intent_summary })
            : t("nav.prevNumberOnly", { number: shot.number })
          : side === "prev"
            ? t("nav.noPrev")
            : t("nav.noNext")
      }
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-lg border border-ui-border px-1.5 py-1 text-ui-text-secondary transition-colors",
        disabled ? "cursor-not-allowed opacity-30" : "hover:border-white/30 hover:bg-ui-surface-raised",
      )}
    >
      {side === "prev" && <Arrow className="h-3.5 w-3.5 text-ui-text-muted" />}
      <div className="h-9 w-12 overflow-hidden rounded-md ring-1 ring-white/5 dark:bg-zinc-800 light:bg-clay-2">
        {posterUrl ? (
          <img src={posterUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-ui-text-muted tabular-nums">
            {shot ? shot.number : "—"}
          </div>
        )}
      </div>
      {side === "next" && <Arrow className="h-3.5 w-3.5 text-ui-text-muted" />}
    </button>
  )
}

function externalPromptTarget(raw: string): "image" | "video" | "shooting_board" {
  if (raw === "video" || raw === "shooting_board") return raw
  return "image"
}

export function ShotPanel() {
  const { t } = useTranslation(["shot", "common", "workspace", "presets", "flow", "layout"])
  const {
    open,
    projectId,
    shotId,
    leftPanel,
    shotViewMode,
    setLeftPanel,
    setShotViewMode,
    setPreviewTab,
    composerTab,
    setComposerTab,
    clearPendingDefaultTab,
    close,
    closeRequestId,
    openShotPanel,
    setWorkspaceBootstrap,
    workspaceBootstrap,
  } = useShotPanelStore()
  const roomViewerActive = useShotPanelStore((s) => s.roomViewerReturnClose != null)
  const page = usePage<Partial<WorkspaceBaseProps>>()
  const pageProject = page.props.project
  const {
    project: workspaceProject,
    project_permissions: workspacePermissions,
    bootstrapPending,
  } = useShotPanelWorkspace()
  const jobs = useWorkspaceJobsStore((s) => s.jobs)
  const [meta, setMeta] = useState<ShotMeta | null>(null)
  /** Seed for ShotPreviewView — avoids a third GET /detail after late mount. */
  const [detailShot, setDetailShot] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [presetWizardOpen, setPresetWizardOpen] = useState(false)
  const canSaveAsPreset =
    projectSectionAllowed(workspaceProject?.type_config, "presets") &&
    workspacePermissions?.can_edit_content === true &&
    shotId != null
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0)
  const [agentActionBusy, setAgentActionBusy] = useState(false)
  const isDesktop = useWorkspaceEntitySplitDesktop()
  const setJobsOpen = useUiStore((s) => s.setJobsOpen)
  const {
    message: shotAutoSendMessage,
    selectedShotId: pendingShotId,
    clear: clearPendingAgentAutoSend,
  } = usePendingAgentAutoSend("shot", projectId ?? workspaceProject?.id ?? 0)
  const shotPanelAutoSend = open && shotId != null && pendingShotId === shotId ? shotAutoSendMessage : null
  const [siblings, setSiblings] = useState<{ prev: ShotSiblingSummary | null; next: ShotSiblingSummary | null }>({
    prev: null,
    next: null,
  })
  const restoredRef = useRef(false)
  const promptViewRef = useRef<StoryboardPromptViewHandle>(null)
  const [mentionItems, setMentionItems] = useState<MentionItem[]>([])
  const [dirtyStoryboard, setDirtyStoryboard] = useState(false)
  const [dirtyVideo, setDirtyVideo] = useState(false)
  const [generationRefs, setGenerationRefs] = useState<ShotGenerationRefs | null>(null)
  // Спиннер на вкладках дока Кадр / Видео / Раскадровка, пока идёт их генерация.
  const busyComposerTabs = useBusyTabs((jobs) => (shotId != null ? busyShotTabKeys(jobs, shotId) : []))
  const [viewRailHovered, setViewRailHovered] = useState(false)
  const [extrasHost, setExtrasHost] = useState<HTMLElement | null>(null)
  const [desktopComposerHost, setDesktopComposerHost] = useState<HTMLElement | null>(null)
  const [mobileComposerHost, setMobileComposerHost] = useState<HTMLElement | null>(null)
  const [storyboardGenerateHost, setStoryboardGenerateHost] = useState<HTMLElement | null>(null)
  const [videoGenerateHost, setVideoGenerateHost] = useState<HTMLElement | null>(null)
  const [shootingBoardGenerateHost, setShootingBoardGenerateHost] = useState<HTMLElement | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [leftColumnEl, setLeftColumnEl] = useState<HTMLDivElement | null>(null)
  const [leftWidthPct, setLeftWidthPct] = useState(readStoredLeftWidthPct)
  const leftWidthPctRef = useRef(leftWidthPct)
  leftWidthPctRef.current = leftWidthPct
  const resizeRef = useRef<{
    active: boolean
    startX: number
    startPct: number
    dragging: boolean
  }>({ active: false, startX: 0, startPct: LEFT_WIDTH_DEFAULT, dragging: false })

  // Меню слева остаётся видимым и на время шота сворачивается (сохранённую настройку не трогаем).
  // Не вешать атрибут на data-workspace-fullscreen-open: его ставит и редактор на странице 3D-сцен.
  useEffect(() => {
    document.documentElement.toggleAttribute("data-shot-panel-open", open)
    useUiStore.getState().setShotPanelOpen(open)
    return () => {
      document.documentElement.removeAttribute("data-shot-panel-open")
      useUiStore.getState().setShotPanelOpen(false)
    }
  }, [open])

  // Вне workspace-страниц (monitoring) нет project в Inertia props — подгружаем bootstrap.
  useEffect(() => {
    if (!open || !projectId) return
    if (pageProject?.id === projectId) {
      if (workspaceBootstrap) setWorkspaceBootstrap(null)
      return
    }
    if (workspaceBootstrap?.project.id === projectId) return
    let cancelled = false
    void fetchShotPanelBootstrap(projectId)
      .then((data) => {
        if (!cancelled && useShotPanelStore.getState().open) {
          setWorkspaceBootstrap(data)
        }
      })
      .catch(() => {
        if (!cancelled) toast.error(t("panel.loadFailed"))
      })
    return () => {
      cancelled = true
    }
    // workspaceBootstrap читаем для early-return / clear; намеренно не в deps целиком —
    // иначе после setBootstrap эффект лишний раз перезапускается.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [open, projectId, pageProject?.id, setWorkspaceBootstrap, t])

  useEffect(() => {
    if (!open) return
    useUiStore.getState().setMobileNavOpen(false)
  }, [open])

  // Layout-effect, а не обычный effect: при полной перезагрузке страницы с
  // #shot=… панель должна открыться до первой отрисовки, иначе на мгновение
  // виден сториборд под ней.
  useLayoutEffect(() => {
    if (restoredRef.current) return
    restoredRef.current = true
    const pid = pageProject?.id
    if (!pid) return
    const parsed = parseShotHash(window.location.hash)
    if (parsed) openShotPanel({ projectId: pid, ...parsed })
  }, [pageProject?.id, openShotPanel])

  useEffect(() => {
    const onHashChange = () => {
      const pid = pageProject?.id
      if (!pid) return
      const parsed = parseShotHash(window.location.hash)
      if (parsed) {
        openShotPanel({ projectId: pid, ...parsed })
      } else if (open && !isWorkspaceEntityHash(window.location.hash)) {
        close()
      }
    }
    window.addEventListener("hashchange", onHashChange)
    return () => window.removeEventListener("hashchange", onHashChange)
  }, [pageProject?.id, open, openShotPanel, close])

  // Уход со shot-path / смена episode — закрываем синхронно в beforeUpdate
  // (до swapComponent), иначе дети читают project.frame_format при props без project.
  // #shot= на characters — оставляем. storyboard↔video — оба shot-path, не закрываем.
  // Переход storyboard → video без hash: после navigate восстанавливаем hash из стора.
  useEffect(() => {
    let lastEpisodeId = new URLSearchParams(window.location.search).get("episode_id")
    let lastPath = window.location.pathname

    const onBeforeUpdate = (event: { detail: { page: { url: string } } }) => {
      const next = new URL(event.detail.page.url, window.location.origin)
      const nextPath = next.pathname
      const nextEpisodeId = next.searchParams.get("episode_id")
      const hashShot = parseShotHash(next.hash)
      const { open: isOpen, close: closePanel } = useShotPanelStore.getState()

      const episodeChanged = lastEpisodeId !== nextEpisodeId
      lastEpisodeId = nextEpisodeId

      if (isOpen && episodeChanged) {
        closePanel()
        lastPath = nextPath
        return
      }

      if (nextPath !== lastPath) {
        const leftShotPath = isShotPanelPagePath(lastPath) && !isShotPanelPagePath(nextPath)
        if (isOpen && leftShotPath && !hashShot) closePanel()
        lastPath = nextPath
      }
    }

    const syncHashWithStore = () => {
      // microtask: даём Inertia дописать URL; close уже в beforeUpdate.
      queueMicrotask(() => {
        const {
          open: isOpen,
          projectId: pid,
          shotId: sid,
          previewTab,
          leftPanel: lp,
          shotViewMode: mode,
        } = useShotPanelStore.getState()
        const currentHash = window.location.hash
        const path = window.location.pathname

        // Не на shot-path — hash не восстанавливаем (characters#shot= открывается отдельно).
        if (!isShotPanelPagePath(path)) {
          return
        }
        if (isWorkspaceEntityHash(currentHash)) {
          return
        }
        if (!isOpen || !pid || !sid) {
          // Панель закрыта, а в URL остался stale #shot=… — убираем.
          if (parseShotHash(currentHash)) {
            window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search)
          }
          return
        }
        const hash = shotPanelHash(sid, previewTab, lp, mode)
        if (window.location.hash !== hash) {
          window.history.replaceState(
            window.history.state,
            "",
            window.location.pathname + window.location.search + hash,
          )
        }
      })
    }
    const offBeforeUpdate = router.on("beforeUpdate", onBeforeUpdate)
    const offNavigate = router.on("navigate", syncHashWithStore)
    const offFinish = router.on("finish", syncHashWithStore)
    return () => {
      offBeforeUpdate()
      offNavigate()
      offFinish()
    }
  }, [])

  useEffect(() => {
    if (!open || !projectId || !shotId) {
      setMeta(null)
      setDetailShot(null)
      return
    }
    setMeta(null)
    setDetailShot(null)
    setDirtyStoryboard(false)
    setDirtyVideo(false)
    setSiblings({ prev: null, next: null })
    let cancelled = false
    setLoading(true)
    fetchShotWorkspaceDetail(projectId, shotId)
      .then(({ shot, sceneContext }) => {
        // open=false выставляется в close() синхронно; cancelled — только после
        // cleanup эффекта. Без проверки open успеваем вызвать setPreviewTab /
        // markShotSeen→reload и вернуть hash уже закрытой панели.
        if (cancelled || !useShotPanelStore.getState().open) return
        const raw = shot as unknown as Record<string, unknown>
        const nextMeta = buildShotMeta(raw, sceneContext)
        setMeta(nextMeta)
        setDetailShot(raw)
        // Default preview tab by status: read generation_unseen BEFORE mark_shot_seen clears it.
        if (useShotPanelStore.getState().pendingDefaultTab && !useShotPanelStore.getState().explicitPreviewTab) {
          const nextPreview = computeDefaultPreviewTab(nextMeta)
          setPreviewTab(nextPreview)
          const { composerTab: currentComposer } = useShotPanelStore.getState()
          setComposerTab(composerTabForPreview(nextPreview, currentComposer))
        }
        clearPendingDefaultTab()
        void markShotSeen(projectId, shotId).then(({ changed }) => {
          if (cancelled || !useShotPanelStore.getState().open) return
          // Повторное открытие уже просмотренного — tree не трогаем.
          if (!changed) return
          // На аниматике tree-reload роняет плеер (эпизод/пейлоад мерцают → дропзона).
          // Бейджи «непросмотрено» здесь не стоят срыва страницы.
          if (window.location.pathname.includes("/workspace/animatics")) return
          // Вне storyboard/studio/… (например monitoring/generations) prop `tree` нет —
          // partial reload ломает Inertia-страницу.
          if (!isShotPanelPagePath(window.location.pathname)) return
          // Нужно только обновить бейджи «непросмотрено» на плитках; preserveUrl —
          // чтобы завершившийся reload не вернул в адресную строку stale hash.
          router.reload({ only: ["tree"], preserveUrl: true })
        })
      })
      .catch(() => {
        if (!cancelled) {
          setMeta(null)
          setDetailShot(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId, shotId, setPreviewTab, setComposerTab, clearPendingDefaultTab])

  useEffect(() => {
    if (!open || !projectId || !shotId) return
    let cancelled = false
    fetchShotSiblings(projectId, shotId)
      .then((data) => {
        if (!cancelled) setSiblings(data)
      })
      .catch(() => {
        if (!cancelled) setSiblings({ prev: null, next: null })
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId, shotId])

  const counts = useMemo(() => countsFromMeta(meta), [meta])

  const afterPromptFlush = useCallback((action: () => void) => {
    const flush = promptViewRef.current?.flushPromptSave()
    if (!flush) {
      action()
      return
    }
    void flush.then((saved) => {
      if (saved) action()
    })
  }, [])

  const handledCloseRequestId = useRef(closeRequestId)
  useEffect(() => {
    if (closeRequestId === handledCloseRequestId.current) return
    handledCloseRequestId.current = closeRequestId
    afterPromptFlush(close)
  }, [closeRequestId, afterPromptFlush, close])

  useEffect(() => {
    if (!shotPanelAutoSend || !isDesktop) return
    setShotViewMode("classic")
    setComposerTab("assistant")
  }, [shotPanelAutoSend, isDesktop, setShotViewMode, setComposerTab])

  const openAssistant = useCallback(() => {
    afterPromptFlush(() => {
      setLeftPanel("prompt")
    })
  }, [afterPromptFlush, setLeftPanel])

  const flowSections = workspaceProject?.type_config?.allowed_sections
  const showFlow = !flowSections || flowSections.length === 0 || flowSections.includes("flow")
  const viewMode: ShotViewMode = (shotViewMode === "flow" || leftPanel === "flow") && showFlow ? "flow" : "classic"
  const effectiveLeftPanel: ShotLeftPanelKey = leftPanel === "assistant" || leftPanel === "flow" ? "prompt" : leftPanel
  const leftIsHome = effectiveLeftPanel === "prompt"
  const isComposerSurface = viewMode !== "flow" && leftIsHome
  const selectViewMode = useCallback(
    (mode: ShotViewMode) => {
      if (mode === viewMode && leftIsHome) return
      afterPromptFlush(() => setShotViewMode(mode))
    },
    [afterPromptFlush, leftIsHome, setShotViewMode, viewMode],
  )
  const flowBoard = useShotFlowBoard({
    enabled: open && viewMode === "flow",
    project: workspaceProject ?? null,
    shotId,
    canEdit: workspacePermissions?.can_edit_content === true,
  })
  const selectedColumns = shotPanelColumns(viewMode, leftIsHome, effectiveLeftPanel)
  const [shownColumns, setShownColumns] = useState(selectedColumns)
  const [trackedShotId, setTrackedShotId] = useState(shotId)
  const [promptReady, setPromptReady] = useState(false)
  const [previewReady, setPreviewReady] = useState(false)
  const [entityReadyId, setEntityReadyId] = useState<ShotLeftColumnId | null>(null)
  const selectedLeftRef = useRef(selectedColumns.left)
  selectedLeftRef.current = selectedColumns.left
  if (shotId !== trackedShotId) {
    setTrackedShotId(shotId)
    setShownColumns(selectedColumns)
    setPromptReady(false)
    setPreviewReady(false)
    setEntityReadyId(null)
  }
  const previousSelectedLeftRef = useRef(selectedColumns.left)
  if (previousSelectedLeftRef.current !== selectedColumns.left) {
    previousSelectedLeftRef.current = selectedColumns.left
    if (entityReadyId !== null) setEntityReadyId(null)
  }
  const markEntityReady = useCallback((id: ShotLeftColumnId) => {
    if (selectedLeftRef.current !== id) return
    setEntityReadyId(id)
  }, [])
  const markPromptReady = useCallback(() => setPromptReady(true), [])
  const markPreviewReady = useCallback(() => setPreviewReady(true), [])

  const selectComposerTab = useCallback(
    (tab: ComposerTabKey) => {
      setComposerTab(tab)
      if (tab === "storyboard") {
        setDirtyStoryboard(false)
        setPreviewTab("storyboard")
      }
      if (tab === "video") {
        setDirtyVideo(false)
        setPreviewTab("video")
      }
      if (tab === "shooting_board") {
        setPreviewTab("shooting_board")
      }
    },
    [setComposerTab, setPreviewTab],
  )

  useEffect(() => {
    if (composerTab === "storyboard") setDirtyStoryboard(false)
    if (composerTab === "video") setDirtyVideo(false)
  }, [composerTab])

  const markPromptDirty = useCallback(
    (target: "image" | "video") => {
      if (target === "video") {
        if (composerTab !== "video") setDirtyVideo(true)
        return
      }
      if (composerTab !== "storyboard") setDirtyStoryboard(true)
    },
    [composerTab],
  )

  const handleSwitchView = useCallback(
    (key: ShotModalKey) => {
      const next = key === "preview" ? "prompt" : (key as ShotLeftPanelKey)
      if (leftPanel === "prompt" && next !== "prompt") {
        afterPromptFlush(() => setLeftPanel(next))
        return
      }
      setLeftPanel(next)
    },
    [afterPromptFlush, leftPanel, setLeftPanel],
  )

  const handleDataChanged = useCallback(() => {
    // Точечный reload: полный сбрасывает аниматик/таймлайн на странице Animatics
    // (пересоздаёт video_stream_url / timeline_frame_urls identity). tree — плитки
    // и бейджи; jobs — статусы генерации после действий в панели.
    router.reload({ only: ["tree", "jobs"], preserveUrl: true })
    setPreviewRefreshKey((k) => k + 1)
    if (!projectId || !shotId) return
    fetchShotWorkspaceDetail(projectId, shotId)
      .then(({ shot, sceneContext }) => {
        const raw = shot as unknown as Record<string, unknown>
        setMeta(buildShotMeta(raw, sceneContext))
        // Keep seed in sync if ShotPreviewView remounts after this refresh.
        setDetailShot(raw)
      })
      .catch(() => {})
  }, [projectId, shotId])

  const handleAgentShotPromptUpdated = useCallback(
    (payload: {
      shotId: number
      target: string
      improvedPrompt: string
      cameraPresetIds?: number[]
      panelCount?: number
    }) => {
      if (!shotId || payload.shotId !== shotId) return
      const target = externalPromptTarget(payload.target)
      promptViewRef.current?.applyExternalPrompt({
        target,
        text: payload.improvedPrompt,
        cameraPresetIds: payload.cameraPresetIds,
        panelCount: payload.panelCount,
      })
      if (target === "image" || target === "video") markPromptDirty(target)
      toast.success(t("workspace:agent.promptUpdated"))
    },
    [markPromptDirty, shotId, t],
  )

  const applyStrippedPrompts = useCallback(
    (payload: {
      storyboardImagePrompt?: string | null
      videoKlingPrompt?: string | null
      strippedPromptTargets?: string[]
    }) => {
      const targets = payload.strippedPromptTargets ?? []
      if (targets.includes("image") && typeof payload.storyboardImagePrompt === "string") {
        promptViewRef.current?.applyExternalPrompt({ target: "image", text: payload.storyboardImagePrompt })
        markPromptDirty("image")
      }
      if (targets.includes("video") && typeof payload.videoKlingPrompt === "string") {
        promptViewRef.current?.applyExternalPrompt({ target: "video", text: payload.videoKlingPrompt })
        markPromptDirty("video")
      }
    },
    [markPromptDirty],
  )

  const handleAgentShotDurationUpdated = useCallback(
    (payload: { shotId: number; durationTargetSec: number }) => {
      if (!shotId || payload.shotId !== shotId || !projectId) return
      fetchShotWorkspaceDetail(projectId, shotId)
        .then(({ shot, sceneContext }) => {
          setMeta(buildShotMeta(shot as unknown as Record<string, unknown>, sceneContext))
          setPreviewRefreshKey((k) => k + 1)
          toast.success(t("workspace:agent.durationUpdated", { sec: payload.durationTargetSec }))
        })
        .catch(() => {})
    },
    [projectId, shotId, t],
  )

  const handleAgentShotVideoReferencesUpdated = useCallback(
    (payload: {
      shotIds: number[]
      updated: { shotId: number; sendAnimatic: boolean; animaticMode: string; useShootingBoard: boolean }[]
    }) => {
      if (!shotId || !payload.shotIds.includes(shotId) || !projectId) return
      const row = payload.updated.find((item) => item.shotId === shotId)
      if (row) {
        promptViewRef.current?.applyExternalSettings({
          sendAnimatic: row.sendAnimatic,
          animaticMode: row.animaticMode,
          useShootingBoard: row.useShootingBoard,
        })
      }
      fetchShotWorkspaceDetail(projectId, shotId)
        .then(({ shot, sceneContext }) => {
          setMeta(buildShotMeta(shot as unknown as Record<string, unknown>, sceneContext))
          setPreviewRefreshKey((k) => k + 1)
          toast.success(t("workspace:agent.videoReferencesUpdated"))
        })
        .catch(() => {
          toast.success(t("workspace:agent.videoReferencesUpdated"))
        })
    },
    [projectId, shotId, t],
  )

  const handleAgentShotCharactersUpdated = useCallback(
    (payload: {
      shotId: number
      storyboardImagePrompt?: string | null
      videoKlingPrompt?: string | null
      strippedPromptTargets?: string[]
    }) => {
      if (!shotId || payload.shotId !== shotId || !projectId) return
      fetchShotWorkspaceDetail(projectId, shotId)
        .then(({ shot, sceneContext }) => {
          setMeta(buildShotMeta(shot as unknown as Record<string, unknown>, sceneContext))
          setPreviewRefreshKey((k) => k + 1)
          applyStrippedPrompts(payload)
          toast.success(t("workspace:agent.charactersUpdated"))
        })
        .catch(() => {})
    },
    [applyStrippedPrompts, projectId, shotId, t],
  )

  const handleAgentShotLocationUpdated = useCallback(
    (payload: {
      shotId: number
      storyboardImagePrompt?: string | null
      videoKlingPrompt?: string | null
      strippedPromptTargets?: string[]
    }) => {
      if (!shotId || payload.shotId !== shotId || !projectId) return
      fetchShotWorkspaceDetail(projectId, shotId)
        .then(({ shot, sceneContext }) => {
          setMeta(buildShotMeta(shot as unknown as Record<string, unknown>, sceneContext))
          setPreviewRefreshKey((k) => k + 1)
          applyStrippedPrompts(payload)
          toast.success(t("workspace:agent.locationUpdated"))
        })
        .catch(() => {})
    },
    [applyStrippedPrompts, projectId, shotId, t],
  )

  const handleAgentShotSceneUpdated = useCallback(
    (payload: { shotId: number }) => {
      if (!shotId || payload.shotId !== shotId || !projectId) return
      handleDataChanged()
    },
    [handleDataChanged, projectId, shotId],
  )

  const handleAgentActionConfirmed = useCallback(
    (res: AgentConfirmActionResult, action: AgentPendingAction) => {
      const tool = action.tool_name
      if (tool === "generate_project_cover") {
        setJobsOpen(true)
        router.reload({ only: ["jobs"] })
        return
      }
      if (tool === "regenerate_keyframe" || tool === "regenerate_video" || tool === "generate_shooting_board") {
        setPreviewRefreshKey((k) => k + 1)
        setJobsOpen(true)
        router.reload({ only: ["jobs"] })
        const result = res.result
        const rawIds = result?.shot_ids
        const shotIds = Array.isArray(rawIds) ? rawIds.filter((id): id is number => typeof id === "number") : []
        if (
          tool === "generate_shooting_board" &&
          result?.send_as_reference === true &&
          shotId &&
          shotIds.includes(shotId)
        ) {
          promptViewRef.current?.applyExternalSettings({ useShootingBoard: true })
        }
      }
    },
    [setJobsOpen, shotId],
  )

  const handleAgentShotCreated = useCallback(
    (payload: { shotId: number; number: string; shotKind: string }) => {
      setAgentActionBusy(true)
      router.reload({
        only: ["tree"],
        onFinish: () => {
          setAgentActionBusy(false)
          if (projectId && payload.shotId > 0) {
            openShotPanel({ projectId, shotId: payload.shotId, preserveTab: true })
          }
        },
      })
    },
    [openShotPanel, projectId],
  )

  const handleAgentShotsBatchQueued = useCallback(
    (_payload: { generationJobId: number; shotsCount: number }) => {
      setJobsOpen(true)
      router.reload({ only: ["jobs", "tree"] })
    },
    [setJobsOpen],
  )

  const navigateToShot = useCallback(
    (id: number) => {
      if (!projectId) return
      afterPromptFlush(() => openShotPanel({ projectId, shotId: id, leftPanel, shotViewMode, preserveTab: true }))
    },
    [afterPromptFlush, projectId, leftPanel, shotViewMode, openShotPanel],
  )

  const endResize = useCallback(() => {
    const state = resizeRef.current
    if (!state.active) return
    const wasDragging = state.dragging
    state.active = false
    state.dragging = false
    document.body.style.removeProperty("user-select")
    document.body.style.removeProperty("cursor")
    if (!wasDragging) return
    // Suppress the click that browsers fire after a drag that started on a sidebar button.
    const stopClick = (ev: MouseEvent) => {
      ev.preventDefault()
      ev.stopPropagation()
    }
    window.addEventListener("click", stopClick, { capture: true, once: true })
    try {
      localStorage.setItem(LEFT_WIDTH_STORAGE_KEY, String(leftWidthPctRef.current))
    } catch {
      // ignore quota / private mode
    }
  }, [])

  const onResizePointerMove = useCallback((e: PointerEvent) => {
    const state = resizeRef.current
    if (!state.active) return
    const dx = e.clientX - state.startX
    if (!state.dragging) {
      if (Math.abs(dx) < RESIZE_DRAG_THRESHOLD_PX) return
      state.dragging = true
      document.body.style.userSelect = "none"
      document.body.style.cursor = "ew-resize"
    }
    const bodyWidth = bodyRef.current?.getBoundingClientRect().width
    if (!bodyWidth || bodyWidth <= 0) return
    const nextPct = clampLeftWidthPct(state.startPct + (dx / bodyWidth) * 100)
    leftWidthPctRef.current = nextPct
    setLeftWidthPct(nextPct)
  }, [])

  const onResizePointerUp = useCallback(() => {
    window.removeEventListener("pointermove", onResizePointerMove)
    window.removeEventListener("pointerup", onResizePointerUp)
    endResize()
  }, [endResize, onResizePointerMove])

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", onResizePointerMove)
      window.removeEventListener("pointerup", onResizePointerUp)
      document.body.style.removeProperty("user-select")
      document.body.style.removeProperty("cursor")
    }
  }, [onResizePointerMove, onResizePointerUp])

  const onSidebarResizePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      // Resize only on desktop three-column layout
      if (!window.matchMedia("(min-width: 768px)").matches) return
      resizeRef.current = {
        active: true,
        startX: e.clientX,
        startPct: leftWidthPctRef.current,
        dragging: false,
      }
      window.addEventListener("pointermove", onResizePointerMove)
      window.addEventListener("pointerup", onResizePointerUp)
    },
    [onResizePointerMove, onResizePointerUp],
  )

  const scene3dDialogRef = useRef<LocationScene3dDialogHandle>(null)
  const [scene3dSession, setScene3dSession] = useState<Scene3dEditorSession | null>(null)
  const scene3dEditorId = scene3dSession?.open ? scene3dSession.scene3dId : null
  const scene3dEditorActive = scene3dEditorId != null
  const promptMounted = !bootstrapPending && (viewMode === "classic" || shownColumns.right === "preview")
  useEffect(() => {
    if (!promptMounted) setPromptReady(false)
  }, [promptMounted])
  useLayoutEffect(() => {
    if (scene3dEditorActive) {
      setShownColumns((prev) => {
        if (prev.left === selectedColumns.left && prev.right === selectedColumns.right) return prev
        return selectedColumns
      })
      return
    }
    const leftReady =
      selectedColumns.left === "composer"
        ? promptReady
        : selectedColumns.left === "flow-side"
          ? flowBoard.ready
          : entityReadyId === selectedColumns.left
    const rightReady = selectedColumns.right === "flow" ? flowBoard.ready : detailShot != null || previewReady
    setShownColumns((prev) => {
      const left = prev.left !== selectedColumns.left && leftReady ? selectedColumns.left : prev.left
      const right = prev.right !== selectedColumns.right && rightReady ? selectedColumns.right : prev.right
      if (left === prev.left && right === prev.right) return prev
      return { left, right }
    })
  }, [scene3dEditorActive, selectedColumns, promptReady, entityReadyId, previewReady, detailShot, flowBoard.ready])
  const scene3dEditorWasActive = useRef(false)

  const handleScene3dSessionChange = useCallback((session: Scene3dEditorSession) => {
    setScene3dSession(session)
  }, [])

  useEffect(() => {
    if (!open) setScene3dSession(null)
  }, [open])

  useEffect(() => {
    if (scene3dEditorActive && !scene3dEditorWasActive.current) {
      setLeftPanel("prompt")
      setComposerTab("assistant")
    }
    scene3dEditorWasActive.current = scene3dEditorActive
  }, [scene3dEditorActive, setLeftPanel, setComposerTab])

  useEffect(() => {
    const { setScene3dReturnClose } = useShotPanelStore.getState()
    if (!open || !scene3dEditorActive) {
      setScene3dReturnClose(null)
      return
    }
    const closeEditor = () => scene3dDialogRef.current?.requestClose()
    setScene3dReturnClose(closeEditor)
    return () => setScene3dReturnClose(null)
  }, [open, scene3dEditorActive])

  const getScene3dFocus = useCallback(() => {
    if (!scene3dEditorActive) return null
    return scene3dDialogRef.current?.agentFocus() ?? null
  }, [scene3dEditorActive])

  const applyScene3dLayout = useCallback(
    (payload: Scene3dLayoutUpdated) => {
      if (!scene3dEditorActive || scene3dEditorId == null || payload.scene3d_id !== scene3dEditorId) return
      scene3dDialogRef.current?.applyServerLayout(payload.layout)
    },
    [scene3dEditorActive, scene3dEditorId],
  )

  const handleScene3dEntityUpdated = useCallback(
    (payload: AgentEntityUpdatedPayload) => {
      if (payload.entityType !== "scene3d") return
      if (payload.deleted && payload.id != null && payload.id === scene3dEditorId) {
        scene3dDialogRef.current?.requestClose()
      }
    },
    [scene3dEditorId],
  )

  if (!open || !projectId || !shotId) return null

  const base = meta ? t("labelWithScene", { scene: meta.sceneNumber, number: meta.number }) : t("label")
  const title = t("panel.title", { base })
  const sidebarCurrent: ShotModalKey = leftIsHome ? "preview" : (effectiveLeftPanel as ShotModalKey)
  const paintFlow = shownColumns.right === "flow"
  const paintFlowSide = shownColumns.left === "flow-side"
  const paintComposer = shownColumns.left === "composer"
  const paintAssistant = isDesktop && paintComposer && composerTab === "assistant"
  const composerKind = composerTab === "video" || composerTab === "shooting_board" ? composerTab : "storyboard"
  const showShootingBoardComposer = meta?.shotKind !== "transition"
  const composerHost = isDesktop
    ? paintComposer && composerTab !== "assistant"
      ? desktopComposerHost
      : null
    : paintComposer
      ? mobileComposerHost
      : null
  const fillColumn = paintComposer && (!isDesktop || composerTab !== "assistant")
  // Generate of the open Frame / Video tab lives in the column footer; the rest stays under the player.
  const columnGenerateKind =
    isDesktop &&
    paintComposer &&
    !scene3dEditorActive &&
    (composerTab === "storyboard" || composerTab === "video" || composerTab === "shooting_board")
      ? composerTab
      : null
  const blockPendingLabel = t("shell.nav.loading", { ns: "layout" })
  const composerTabIconClass = "h-3.5 w-3.5"
  const composerTabs = [
    {
      id: "storyboard",
      label: t("composerTabs.frame"),
      active: composerTab === "storyboard" || (!isDesktop && composerTab === "assistant"),
      dirty: dirtyStoryboard,
      labelCollapsible: true,
      iconWhenCollapsedOnly: true,
      icon: <ImageIcon className={composerTabIconClass} strokeWidth={2} />,
      busy: busyComposerTabs.has("storyboard"),
      onClick: () => selectComposerTab("storyboard"),
    },
    {
      id: "video",
      label: t("composerTabs.video"),
      active: composerTab === "video",
      dirty: dirtyVideo,
      labelCollapsible: true,
      iconWhenCollapsedOnly: true,
      icon: <VideoIcon className={composerTabIconClass} strokeWidth={2} />,
      busy: busyComposerTabs.has("video"),
      onClick: () => selectComposerTab("video"),
    },
    ...(showShootingBoardComposer
      ? [
          {
            id: "shooting_board",
            label: t("tabs.shootingBoard"),
            active: composerTab === "shooting_board",
            labelCollapsible: true,
            iconWhenCollapsedOnly: true,
            icon: <LayoutGrid className={composerTabIconClass} strokeWidth={2} />,
            busy: busyComposerTabs.has("shooting_board"),
            onClick: () => selectComposerTab("shooting_board"),
          },
        ]
      : []),
    {
      id: "assistant",
      label: t("composerTabs.assistant"),
      active: composerTab === "assistant",
      labelCollapsible: true,
      iconWhenCollapsedOnly: true,
      onClick: () => selectComposerTab("assistant"),
    },
  ]
  const agentPanelProps = {
    projectId,
    projectName: workspaceProject?.name,
    shotLabel: meta ? formatShotLabel(meta.sceneNumber, meta.number) : null,
    shotRefs:
      meta && shotId
        ? ([{ id: shotId, label: formatShotLabel(meta.sceneNumber, meta.number) }] satisfies AgentShotRef[])
        : [],
    contextType: "shot" as const,
    contextId: shotId,
    selectedShotId: shotId,
    quickSurface: "shot" as const,
    onOpenShot: (id: number) => {
      if (id === shotId) return
      openShotPanel({ projectId, shotId: id, view: "preview", preserveTab: true })
    },
    onShotPromptUpdated: handleAgentShotPromptUpdated,
    onShotDurationUpdated: handleAgentShotDurationUpdated,
    onShotVideoReferencesUpdated: handleAgentShotVideoReferencesUpdated,
    onShotCharactersUpdated: handleAgentShotCharactersUpdated,
    onShotLocationUpdated: handleAgentShotLocationUpdated,
    onShotSceneUpdated: handleAgentShotSceneUpdated,
    onShotCreated: handleAgentShotCreated,
    onShotsBatchQueued: handleAgentShotsBatchQueued,
    onAgentActionConfirmed: handleAgentActionConfirmed,
    autoSendMessage: shotPanelAutoSend,
    onAutoSendSettled: clearPendingAgentAutoSend,
    mentionItems,
    onQuickCommandUsed: () => selectComposerTab("assistant"),
  }
  const scene3dAgentProps = {
    projectId,
    projectName: workspaceProject?.name,
    mode: "scene3d_assistant" as const,
    title: t("workspace:agent.scene3dTitle"),
    contextType: "scene3d" as const,
    contextId: projectId,
    quickSurface: "scenes3d" as const,
    selectedEntityId: scene3dEditorId,
    selectedEntity: scene3dSession?.name ? { name: scene3dSession.name } : null,
    getScene3dFocus,
    onScene3dLayoutUpdated: applyScene3dLayout,
    onEntityUpdated: handleScene3dEntityUpdated,
    onQuickCommandUsed: () => selectComposerTab("assistant"),
  }
  const columnAgentProps = scene3dEditorActive ? scene3dAgentProps : agentPanelProps

  const renderLeftColumn = (id: ShotLeftColumnId) => {
    if (id === "flow-side") {
      if (!isDesktop) return null
      return (
        <div className="h-full min-h-0 w-full">
          <AgentColumnChromeProvider>{flowBoard.sideColumn}</AgentColumnChromeProvider>
        </div>
      )
    }
    if (id === "composer") {
      if (isDesktop) {
        return (
          <WorkspaceAgentSideColumn
            profileActive={composerTab !== "assistant"}
            pinnedFooter
            tabs={scene3dEditorActive ? [] : composerTabs}
            agent={columnAgentProps}
            dirtyAriaLabel={t("workspace:agent.dock.dirtyAria")}
            className="bg-transparent dark:bg-transparent light:bg-transparent"
            bodySlot={<div ref={setDesktopComposerHost} className="flex h-full min-h-0 flex-col" />}
          >
            <PinnedTabLayers
              mode="stack"
              active={composerTab}
              layers={(
                [
                  ["storyboard", setStoryboardGenerateHost],
                  ["video", setVideoGenerateHost],
                  ["shooting_board", setShootingBoardGenerateHost],
                ] as const
              ).map(([id, setHost]) => ({
                id,
                node: (
                  <PinnedFooterBox className="flex items-center justify-center">
                    <div ref={setHost} className="flex w-full min-w-0 justify-center" />
                  </PinnedFooterBox>
                ),
              }))}
            />
          </WorkspaceAgentSideColumn>
        )
      }
      if (scene3dEditorActive) return null
      return (
        <div className="flex h-full min-h-0 flex-col px-5 pt-4 pb-8">
          <div className="mb-3 flex justify-center">
            <FloatingTabPill
              dirtyAriaLabel={t("workspace:agent.dock.dirtyAria")}
              tabs={composerTabs.filter((tab) => tab.id !== "assistant")}
            />
          </div>
          <div ref={setMobileComposerHost} className="flex h-full min-h-0 flex-1 flex-col" />
        </div>
      )
    }
    if (id === "characters") {
      return (
        <ShotCharactersView
          projectId={projectId}
          shotId={shotId}
          onClose={close}
          onDataChanged={handleDataChanged}
          onInitialLoadSettled={() => markEntityReady("characters")}
        />
      )
    }
    if (id === "location") {
      return (
        <ShotLocationView
          projectId={projectId}
          shotId={shotId}
          onClose={close}
          onDataChanged={handleDataChanged}
          onInitialLoadSettled={() => markEntityReady("location")}
        />
      )
    }
    if (id === "props") {
      return (
        <ShotPropsView
          projectId={projectId}
          shotId={shotId}
          onClose={close}
          onDataChanged={handleDataChanged}
          onInitialLoadSettled={() => markEntityReady("props")}
        />
      )
    }
    if (id === "post_production_versions") {
      return (
        <ShotPostProductionVersionsView
          projectId={projectId}
          shotId={shotId}
          sourceVersionId={meta?.shotId === shotId ? meta.postProductionSourceVersionId : null}
          shotFrameFormat={meta?.frameFormat}
          onDataChanged={handleDataChanged}
          onInitialLoadSettled={() => markEntityReady("post_production_versions")}
        />
      )
    }
    const kind =
      id === "storyboard_versions" ? "storyboard" : id === "shooting_board_versions" ? "shooting_board" : "video"
    return (
      <ShotMediaVersionsView
        projectId={projectId}
        shotId={shotId}
        shotFrameFormat={meta?.frameFormat}
        kind={kind}
        onClose={close}
        onDataChanged={handleDataChanged}
        onInitialLoadSettled={() => markEntityReady(id)}
      />
    )
  }

  return (
    <ShotLeftColumnHostProvider host={leftColumnEl}>
      <WorkspaceFullscreenPanel
        open={open}
        onOpenChange={(o) => {
          if (!o) afterPromptFlush(close)
        }}
        clearShellSidebar
        hideHeader
        bodyClassName="relative min-h-0 overflow-hidden"
      >
        <div
          ref={bodyRef}
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden"
        >
          {/* Left column — prompts, or characters/location panel (full height) */}
          <div
            ref={setLeftColumnEl}
            className={cn(
              "relative order-3 flex h-[100dvh] w-full min-w-0 shrink-0 flex-col border-t border-ui-border md:order-1 md:h-auto md:min-h-0 md:w-auto md:border-t-0",
              roomViewerActive
                ? "absolute inset-0 z-30 overflow-hidden border-0 bg-black px-0 pt-0 pb-0 md:h-full md:w-full"
                : cn(
                    paintFlowSide && !isDesktop && "hidden",
                    paintComposer || paintFlowSide
                      ? "overflow-hidden px-0 pt-0 pb-0 md:h-full"
                      : "px-5 pt-4 pb-8 md:pb-0",
                  ),
              "max-md:!w-full",
            )}
            style={roomViewerActive ? undefined : { width: `${leftWidthPct}%` }}
          >
            <ShotColumnStage
              shownId={shownColumns.left}
              selectedId={selectedColumns.left}
              preloadIncoming={preloadsLeftColumn(selectedColumns.left)}
              label={blockPendingLabel}
              className="h-full"
              render={renderLeftColumn}
            />
          </div>

          {/* Sidebar rail — horizontal on mobile; resize handle on md+ */}
          <div
            className={cn(
              "relative order-2 flex shrink-0 flex-row items-center justify-center overflow-x-auto border-b border-ui-border px-3 py-2 md:cursor-ew-resize md:flex-col md:justify-center md:overflow-x-visible md:border-b-0 md:px-0 md:py-0",
              (scene3dEditorActive || roomViewerActive) && "hidden w-px border-0 px-0 py-0 md:flex",
            )}
            onPointerDown={onSidebarResizePointerDown}
          >
            {scene3dEditorActive ? <div className="absolute inset-y-0 -left-1.5 z-10 w-3 cursor-ew-resize" /> : null}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 bg-ui-border md:block"
            />
            {scene3dEditorActive ? null : showFlow ? (
              <CollapsedNavPeekProvider enabled railHovered={viewRailHovered}>
                <div
                  className="relative flex flex-row items-center gap-2 md:flex-col"
                  onMouseEnter={() => setViewRailHovered(true)}
                  onMouseLeave={() => setViewRailHovered(false)}
                >
                  <ShotViewModeToggle
                    value={viewMode}
                    onChange={selectViewMode}
                    classicLabel={t("sidebar.assistant")}
                    flowLabel={t("sidebar.flow")}
                  />
                  <div className="relative md:rounded-lg md:border md:border-ui-border md:bg-ui-bg-elevated md:p-2">
                    <ShotModalSidebar
                      current={sidebarCurrent}
                      onSwitch={handleSwitchView}
                      counts={counts}
                      className="md:pl-0"
                      embedded
                    />
                  </div>
                </div>
              </CollapsedNavPeekProvider>
            ) : (
              <div className="relative md:rounded-lg md:border md:border-ui-border md:bg-ui-bg-elevated md:p-2">
                <ShotModalSidebar
                  current={sidebarCurrent}
                  onSwitch={handleSwitchView}
                  counts={counts}
                  className="md:pl-0"
                  showAssistant={isDesktop}
                  assistantActive={isComposerSurface}
                  onAssistantClick={openAssistant}
                />
              </div>
            )}
          </div>

          {/* Right column: header + preview. Marked so the 3D editor insets here and leaves the agent column. */}
          <div
            data-workspace-entity-content=""
            className="relative order-1 flex min-h-[100dvh] w-full min-w-0 flex-col md:order-3 md:min-h-0 md:flex-1"
          >
            <div
              className={cn(
                "flex shrink-0 items-center justify-between gap-3 px-5 py-2",
                scene3dEditorActive && "hidden",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <Dialog.Title className="truncate text-base font-semibold text-ui-text-primary">{title}</Dialog.Title>
                  {meta && (
                    <button
                      type="button"
                      onClick={() => setEditOpen(true)}
                      className="shrink-0 rounded-md p-1 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                      aria-label={t("panel.editShot")}
                      title={t("panel.editShot")}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {meta && canSaveAsPreset ? (
                    <ActionHintTooltip label={t("presets:createFromShot")}>
                      <button
                        type="button"
                        onClick={() => setPresetWizardOpen(true)}
                        className="shrink-0 rounded-md p-1 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                        aria-label={t("presets:createFromShot")}
                      >
                        <Bookmark className="h-4 w-4" />
                      </button>
                    </ActionHintTooltip>
                  ) : null}
                  {meta && workspaceProject ? (
                    <>
                      <ShotFrameFormatChip
                        projectId={projectId}
                        shotId={shotId}
                        shotFrameFormat={meta.frameFormat}
                        projectFrameFormat={workspaceProject.frame_format}
                        onSaved={(override) => {
                          setMeta((prev) => (prev ? { ...prev, frameFormat: override } : prev))
                        }}
                        onPersisted={() => {
                          if (!isShotPanelPagePath(window.location.pathname)) return
                          router.reload({ only: ["tree"], preserveUrl: true })
                        }}
                      />
                      <ShotVideoModelChip
                        projectId={projectId}
                        shotId={shotId}
                        shotVideoModelKey={meta.videoModelKey}
                        shotVideoResolution={meta.videoResolution}
                        onSaved={({ videoModelKey, videoResolution }) => {
                          setMeta((prev) => (prev ? { ...prev, videoModelKey, videoResolution } : prev))
                        }}
                      />
                    </>
                  ) : null}
                </div>
                {meta?.intentSummary && (
                  <Dialog.Description className="truncate text-xs text-ui-text-muted">
                    {meta.intentSummary}
                  </Dialog.Description>
                )}
              </div>
              <NavThumb side="prev" shot={siblings.prev} onClick={navigateToShot} />
              <NavThumb side="next" shot={siblings.next} onClick={navigateToShot} />
              <Dialog.Close
                type="button"
                className="shrink-0 rounded-lg p-1.5 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary md:hidden"
                aria-label={t("common.close")}
              >
                <X className="h-5 w-5" />
              </Dialog.Close>
            </div>

            <ShotColumnStage
              shownId={shownColumns.right}
              selectedId={selectedColumns.right}
              preloadIncoming={selectedColumns.right === "preview" && detailShot == null}
              label={blockPendingLabel}
              className={cn(scene3dEditorActive && "absolute inset-0 px-3")}
              slotClassName={cn(
                !paintFlow && !scene3dEditorActive && "it2tv-scrollbar overflow-y-auto px-5 pt-1",
                !paintFlow && !scene3dEditorActive && (isDesktop ? (paintAssistant ? "pb-4" : "pb-16") : "pb-44"),
              )}
              render={(id) =>
                id === "flow" ? (
                  flowBoard.loading || !flowBoard.ready ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
                    </div>
                  ) : (
                    (flowBoard.canvas ?? (
                      <BoardEmptyState
                        icon={<Workflow />}
                        title={t("empty.title", { ns: "flow" })}
                        hint={t("empty.hint", { ns: "flow" })}
                        className="pointer-events-none absolute inset-0 min-h-0"
                      />
                    ))
                  )
                ) : (
                  <div className={scene3dEditorActive ? "relative h-full min-h-0 w-full" : undefined}>
                    {(loading && !meta) || bootstrapPending ? (
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
                      </div>
                    ) : (
                      <ShotPreviewView
                        projectId={projectId}
                        shotId={shotId}
                        jobs={jobs}
                        shotFrameFormat={meta?.frameFormat}
                        shotVideoModelKey={meta?.videoModelKey}
                        shotVideoResolution={meta?.videoResolution}
                        onVideoResolutionChange={(override) => {
                          setMeta((prev) => (prev ? { ...prev, videoResolution: override } : prev))
                        }}
                        sceneNumber={meta?.sceneNumber}
                        refreshKey={previewRefreshKey}
                        initialShotRaw={detailShot}
                        onDataChanged={handleDataChanged}
                        onInitialLoadSettled={markPreviewReady}
                        beforeGenerateVideo={async () => {
                          const flushed = await (promptViewRef.current?.flushPromptSave() ?? Promise.resolve(true))
                          return flushed
                        }}
                        onVideoModelSaved={({ videoModelKey, videoResolution }) => {
                          setMeta((prev) => (prev ? { ...prev, videoModelKey, videoResolution } : prev))
                        }}
                        beforeGenerateStoryboard={() =>
                          promptViewRef.current?.flushPromptSave() ?? Promise.resolve(true)
                        }
                        generationRefs={generationRefs}
                        onGenerationRefsChange={(patch) => promptViewRef.current?.applyGenerationRefs(patch)}
                        confirmMotionReferences={() =>
                          promptViewRef.current?.confirmMotionReferences() ?? Promise.resolve(true)
                        }
                        extrasSlotRef={setExtrasHost}
                        storyboardGenerateHost={columnGenerateKind === "storyboard" ? storyboardGenerateHost : null}
                        videoGenerateHost={columnGenerateKind === "video" ? videoGenerateHost : null}
                        shootingBoardGenerateHost={
                          columnGenerateKind === "shooting_board" ? shootingBoardGenerateHost : null
                        }
                        scene3dDialogRef={scene3dDialogRef}
                        onScene3dSessionChange={handleScene3dSessionChange}
                      />
                    )}
                  </div>
                )
              }
            />

            {paintFlow && !isDesktop ? flowBoard.dock : null}
            {paintFlow ? flowBoard.overlays : null}

            {isDesktop && !paintComposer && !paintFlow ? (
              <Button
                type="button"
                size="sm"
                className={cn(
                  "absolute bottom-4 left-1/2 z-10 -translate-x-1/2 gap-1.5 shadow-lg",
                  UI_ACCENT_FILLED,
                  "dark:shadow-violet-600/30",
                )}
                onClick={openAssistant}
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                {t("sidebar.assistant")}
              </Button>
            ) : null}

            {isDesktop || scene3dEditorActive || paintFlow ? null : (
              <AgentChatPanel
                {...columnAgentProps}
                zIndexClass="z-[105]"
                overlay
                anchor="parent"
                className="px-5 pb-4"
              />
            )}
          </div>
        </div>
        {promptMounted ? (
          <StoryboardPromptView
            ref={promptViewRef}
            projectId={projectId}
            shotId={shotId}
            onDataChanged={handleDataChanged}
            onInitialLoadSettled={markPromptReady}
            refreshKey={previewRefreshKey}
            shotVideoModelKey={meta?.videoModelKey}
            composerKind={composerKind}
            extrasHost={extrasHost}
            composerHost={composerHost}
            fillColumn={fillColumn}
            onMentionItemsChange={setMentionItems}
            onGenerationRefsChange={setGenerationRefs}
          />
        ) : null}
        {agentActionBusy && (
          <div className="pointer-events-none absolute inset-0 z-[106] flex items-center justify-center bg-black/25 backdrop-blur-[1px]">
            <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface px-3 py-2 text-xs text-ui-text-primary shadow-lg">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("workspace:agent.startingAction")}
            </div>
          </div>
        )}
      </WorkspaceFullscreenPanel>
      {meta && (
        <ShotEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          projectId={projectId}
          shotId={shotId}
          sceneId={meta.sceneId}
          label={`${meta.sceneNumber}.${meta.number}`}
          description={meta.intentSummary}
          hidden={meta.hidden}
          onDataChanged={handleDataChanged}
          onDeleted={() => {
            setEditOpen(false)
            handleDataChanged()
            close()
          }}
        />
      )}
      {canSaveAsPreset && projectId != null && shotId != null ? (
        <ShotPresetWizard
          open={presetWizardOpen}
          onOpenChange={setPresetWizardOpen}
          projectId={projectId}
          shotId={shotId}
          nested={2}
          onSaved={(presetId) => {
            setPresetWizardOpen(false)
            close()
            router.visit(`/projects/${projectId}/workspace/presets?preset=${presetId}`)
          }}
        />
      ) : null}
    </ShotLeftColumnHostProvider>
  )
}
